import type { AdminEnvelope, AdminErrorBody } from "@coco-mailrelay/shared-types";
import { useAuthStore } from "../store/auth.store";
import { GaCancelledError, promptForGaCode } from "./ga-interceptor";

/**
 * Unified admin API client (docs/305 §4.3). Every page goes through this — no
 * raw fetch. Responsibilities:
 *   - prefix `/admin`, attach the Bearer token from the auth store
 *   - unwrap the control-plane envelope `{ success, data }` → returns `data`
 *   - throw `ApiRequestError` (carrying `code`) on `{ success: false }`
 *   - centralize 401 → clear the auth store + redirect to `/login`
 *   - centralize 428 STEP_UP_REQUIRED → prompt for TOTP, retry once with
 *     `x-ga-code` (transparent for high-risk operations)
 */

const BASE_URL = "/admin";

/** Error thrown for any non-success response; `code` is the admin error code. */
export class ApiRequestError extends Error {
  readonly code?: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  /** JSON-serializable body; serialized automatically. */
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  /** Explicit Bearer token (login-step pending tokens) overriding the store. */
  authToken?: string;
  /** @internal — guards against infinite 428 retry loops. */
  _gaRetried?: boolean;
}

let isRedirecting = false;

function buildUrl(path: string, params?: ApiOptions["params"]): string {
  let url = `${BASE_URL}${path}`;
  if (params) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) search.set(k, String(v));
    }
    const qs = search.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

export async function apiRequest<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { params, body, authToken, _gaRetried, headers: rawHeaders, ...rest } = options;
  const headers: Record<string, string> = { ...(rawHeaders as Record<string, string> | undefined) };

  if (body !== undefined) headers["Content-Type"] = "application/json";

  const token = authToken ?? useAuthStore.getState().token;
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(buildUrl(path, params), {
    ...rest,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  // 204 No Content — nothing to unwrap (delete endpoints).
  if (response.status === 204) return undefined as T;

  // Centralized 401: login-flow auth endpoints surface their own error (e.g.
  // "invalid code") to their form; everything else clears the session and
  // bounces to /login. `/auth/me` is the session-restoration probe — a 401
  // there means the httpOnly cookie is gone/expired, so it must go through the
  // centralized path (clear the persisted `isAuthenticated` + redirect), not be
  // swallowed by its caller.
  if (response.status === 401) {
    if (path.startsWith("/auth/") && path !== "/auth/me") {
      const errBody = await safeJson(response);
      throw toError(response.status, errBody);
    }
    if (!isRedirecting) {
      isRedirecting = true;
      setTimeout(() => {
        isRedirecting = false;
      }, 3000);
      useAuthStore.getState().logout();
      try {
        await fetch(`${BASE_URL}/auth/logout`, { method: "POST", credentials: "include" });
      } catch {
        // best effort — cookie clear may fail offline
      }
      window.location.href = "/login";
    }
    throw new ApiRequestError("Unauthorized", 401, "UNAUTHORIZED");
  }

  // Centralized 428 step-up: prompt once, retry with x-ga-code.
  if (response.status === 428 && !_gaRetried) {
    let code: string;
    try {
      code = await promptForGaCode();
    } catch (err) {
      if (err instanceof GaCancelledError) throw err;
      throw err;
    }
    return apiRequest<T>(path, {
      ...options,
      _gaRetried: true,
      headers: { ...(rawHeaders as Record<string, string> | undefined), "x-ga-code": code },
    });
  }

  const parsed = (await safeJson(response)) as AdminEnvelope<T> | undefined;

  if (!response.ok || (parsed && parsed.success === false)) {
    throw toError(response.status, parsed);
  }

  // Success envelope.
  if (parsed && parsed.success === true) return parsed.data;
  return undefined as T;
}

async function safeJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function toError(status: number, parsed: unknown): ApiRequestError {
  const envelope = parsed as { success?: boolean; error?: AdminErrorBody } | undefined;
  const error = envelope?.error;
  return new ApiRequestError(error?.message ?? "Request failed", status, error?.code, error?.details);
}
